import React from "react";
import { Badge, Tag, Typography, Empty } from "antd";
import { Droppable } from "@hello-pangea/dnd";
import type { GridColumnMetadata } from "../../types";
import { KanbanCard, type KanbanCardLabels } from "./KanbanCard";

const { Text } = Typography;

interface KanbanColumnProps {
  columnValue: string;
  columnLabel: string;
  items: Record<string, unknown>[];
  columns: GridColumnMetadata[];
  groupByField: string;
  listLabelLookup: Record<string, Record<string, string>>;
  color: string;
  emptyText: string;
  locale: string;
  promotingItemId: string | null;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  cardLabels: KanbanCardLabels;
}

/**
 * A single Kanban column (droppable zone).
 * Renders a header with label + count, and a list of draggable cards.
 */
export const KanbanColumn = React.memo(function KanbanColumn({
  columnValue,
  columnLabel,
  items,
  columns,
  groupByField,
  listLabelLookup,
  color,
  emptyText,
  locale,
  promotingItemId,
  onView,
  onEdit,
  cardLabels,
}: KanbanColumnProps) {
  return (
    <div
      style={{
        minWidth: 280,
        maxWidth: 320,
        flex: "0 0 280px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Column Header */}
      <div
        style={{
          padding: "8px 12px",
          borderTop: `3px solid ${color}`,
          borderRadius: "8px 8px 0 0",
          backgroundColor: "#fafafa",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tag color={color} style={{ margin: 0 }}>
          {columnLabel}
        </Tag>
        <Badge
          count={items.length}
          showZero
          color={items.length > 0 ? color : "#d9d9d9"}
          style={{ fontSize: 11 }}
        />
      </div>

      {/* Droppable Area */}
      <Droppable droppableId={columnValue}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              flex: 1,
              minHeight: 120,
              padding: 8,
              backgroundColor: snapshot.isDraggingOver ? "#e6f7ff" : "#f5f5f5",
              borderRadius: "0 0 8px 8px",
              transition: "background-color 0.2s ease",
              overflowY: "auto",
              maxHeight: "calc(100vh - 300px)",
            }}
          >
            {items.length === 0 && !snapshot.isDraggingOver && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {emptyText}
                  </Text>
                }
                style={{ margin: "16px 0" }}
              />
            )}

            {items.map((item, index) => (
              <KanbanCard
                key={String(item.id)}
                record={item}
                columns={columns}
                groupByField={groupByField}
                listLabelLookup={listLabelLookup}
                index={index}
                locale={locale}
                isPromoting={String(item.id) === promotingItemId}
                onView={onView}
                onEdit={onEdit}
                labels={cardLabels}
              />
            ))}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
});
